import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { usePermissions } from "../hooks/usePermissions";
import {
    Truck, Package, AlertTriangle, ShieldOff, Plus, Trash2, Edit2, X,
    Loader2, Power, CheckCircle2, XCircle, ArrowRight, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

const ORDER_STATUSES = ["Pending", "Allocated", "Shipped", "Delivered", "Override Pending", "Cancelled"];
const FULFILLMENT_TYPES = ["Internal", "Drop Ship"];

export default function Logistics() {
    const perm = usePermissions();
    const [summary, setSummary] = useState(null);
    const [inventory, setInventory] = useState([]);
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [overrideQueue, setOverrideQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [tab, setTab] = useState("inventory"); // inventory | orders
    const [fulfillmentFilter, setFulfillmentFilter] = useState("All"); // All | Internal | Drop Ship

    // modal states
    const [editInv, setEditInv] = useState(null); // null=closed, {}=create, {...}=edit
    const [invForm, setInvForm] = useState({});
    const [editOrder, setEditOrder] = useState(null);
    const [orderForm, setOrderForm] = useState({});
    const [overrideTarget, setOverrideTarget] = useState(null); // {action: "approve"|"reject", order}
    const [overrideReason, setOverrideReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [modalErr, setModalErr] = useState("");

    const refresh = async () => {
        try {
            const [s, inv, ord, p, q] = await Promise.all([
                api.get("/logistics/summary").then((r) => r.data),
                api.get("/inventory").then((r) => r.data),
                api.get("/orders").then((r) => r.data),
                api.get("/products").then((r) => r.data),
                perm.canSeeApprovalQueue
                    ? api.get("/dashboard/order-override-queue").then((r) => r.data).catch(() => [])
                    : Promise.resolve([]),
            ]);
            setSummary(s);
            setInventory(inv);
            setOrders(ord);
            setProducts(p);
            setOverrideQueue(q);
        } catch (err) {
            const status = err?.response?.status;
            if (status !== 401) {
                setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Failed to load logistics data");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
    const invBySku = useMemo(() => Object.fromEntries(inventory.map((i) => [i.sku, i])), [inventory]);

    const filteredOrders = useMemo(() => {
        if (fulfillmentFilter === "All") return orders;
        return orders.filter((o) => o.fulfillment_type === fulfillmentFilter);
    }, [orders, fulfillmentFilter]);

    // ---------- Inventory CRUD ----------
    const openCreateInv = () => {
        setInvForm({ sku: "", product_id: "", quantity_on_hand: 0, reserved_quantity: 0, location: "Warehouse A / Bin 01" });
        setEditInv({});
        setModalErr("");
    };
    const openEditInv = (row) => {
        setInvForm({
            sku: row.sku,
            product_id: row.product_id,
            quantity_on_hand: row.quantity_on_hand,
            reserved_quantity: row.reserved_quantity,
            location: row.location,
        });
        setEditInv(row);
        setModalErr("");
    };
    const closeInv = () => setEditInv(null);

    const submitInv = async (e) => {
        e.preventDefault();
        setBusy(true);
        setModalErr("");
        try {
            const payload = {
                ...invForm,
                quantity_on_hand: Number(invForm.quantity_on_hand),
                reserved_quantity: Number(invForm.reserved_quantity),
            };
            if (editInv && editInv.id) {
                // Don't send sku on update — it's the PK and immutable.
                const { sku, ...rest } = payload;
                await api.patch(`/inventory/${editInv.id}`, rest);
                toast.success("Inventory updated");
            } else {
                await api.post("/inventory", payload);
                toast.success("Inventory added");
            }
            closeInv();
            refresh();
        } catch (err) {
            setModalErr(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setBusy(false);
        }
    };

    const deleteInv = async (row) => {
        if (!window.confirm(`Delete SKU ${row.sku}? This cannot be undone.`)) return;
        try {
            await api.delete(`/inventory/${row.id}`);
            toast.success("SKU removed");
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        }
    };

    // ---------- Order CRUD ----------
    const openCreateOrder = () => {
        const firstSku = inventory[0]?.sku || "";
        setOrderForm({ customer_name: "", sku: firstSku, fulfillment_type: "Internal", quantity: 1 });
        setEditOrder({});
        setModalErr("");
    };
    const closeOrder = () => setEditOrder(null);

    const submitOrder = async (e) => {
        e.preventDefault();
        setBusy(true);
        setModalErr("");
        try {
            const payload = { ...orderForm, quantity: Number(orderForm.quantity) };
            const { data } = await api.post("/orders", payload);
            if (data.order_status === "Override Pending") {
                toast.warning(`${data.order_id} created — Mission Ready Lock engaged. Sent to Override Queue.`);
            } else {
                toast.success(`${data.order_id} created`);
            }
            closeOrder();
            refresh();
        } catch (err) {
            setModalErr(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setBusy(false);
        }
    };

    const transitionOrder = async (order, nextStatus) => {
        try {
            await api.patch(`/orders/${order.order_id}`, { order_status: nextStatus });
            toast.success(`${order.order_id} → ${nextStatus}`);
            refresh();
        } catch (err) {
            const detail = formatApiErrorDetail(err?.response?.data?.detail) || err?.message;
            toast.error(detail);
        }
    };

    const submitOverride = async () => {
        if (!overrideTarget) return;
        const trimmed = overrideReason.trim();
        if (trimmed.length < 3) {
            setModalErr("Reason is required (min 3 characters).");
            return;
        }
        setBusy(true);
        setModalErr("");
        try {
            const path = overrideTarget.action === "approve" ? "approve-override" : "reject-override";
            await api.post(`/orders/${overrideTarget.order.order_id}/${path}`, { reason: trimmed });
            toast.success(overrideTarget.action === "approve" ? "Override approved — order released" : "Override rejected — order cancelled");
            setOverrideTarget(null);
            setOverrideReason("");
            refresh();
        } catch (err) {
            setModalErr(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="logistics-loading">
                <div className="overline">Operations / Logistics</div>
                <div className="heading-display text-4xl">Logistics</div>
                <div className="text-snow-dim font-mono text-sm">Loading warehouse state…</div>
            </div>
        );
    }

    if (error || !summary) {
        return (
            <div className="border border-red-500/40 bg-red-500/5 p-6" data-testid="logistics-error">
                <div className="overline text-red-400">Logistics Unavailable</div>
                <div className="heading-display text-xl mt-2">Could not load warehouse state</div>
                <div className="text-xs text-snow-dim mt-2 font-mono">{typeof error === "string" ? error : "Please retry."}</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="logistics-page">
            {/* Header */}
            <div className="flex items-end justify-between">
                <div>
                    <div className="overline">Operations / Logistics</div>
                    <h1 className="heading-display text-4xl mt-1 flex items-center gap-3">
                        <Truck className="w-8 h-8 text-signal" />
                        Logistics
                    </h1>
                    <p className="text-snow-dim text-sm mt-2 max-w-2xl">
                        Inventory, orders, and fulfillment — gated by the governance system.
                        Mission-Ready Lock blocks shipments on grounded products.
                    </p>
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI testid="kpi-total-skus" label="Total SKUs" value={summary.total_skus} icon={Package} />
                <KPI testid="kpi-low-stock" label="Low Stock" value={summary.low_stock_count} alert={summary.low_stock_count > 0} icon={AlertTriangle} />
                <KPI testid="kpi-pending-shipments" label="Pending Shipments" value={summary.pending_shipments_count} icon={Truck} accent />
                <KPI testid="kpi-blocked-orders" label="Blocked Orders" value={summary.blocked_orders_count} alert={summary.blocked_orders_count > 0} icon={ShieldOff} />
            </div>

            {/* Override Queue (admin/exec only, only if there are blocked orders) */}
            {perm.canSeeApprovalQueue && overrideQueue.length > 0 && (
                <OverrideQueuePanel
                    queue={overrideQueue}
                    onApprove={(o) => { setOverrideTarget({ action: "approve", order: o }); setOverrideReason(""); setModalErr(""); }}
                    onReject={(o) => { setOverrideTarget({ action: "reject", order: o }); setOverrideReason(""); setModalErr(""); }}
                />
            )}

            {/* Tabs */}
            <div className="border-b border-hairline flex items-center gap-1" data-testid="logistics-tabs">
                <TabBtn active={tab === "inventory"} onClick={() => setTab("inventory")} testid="tab-inventory">
                    Inventory ({inventory.length})
                </TabBtn>
                <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="tab-orders">
                    Orders ({orders.length})
                </TabBtn>
            </div>

            {tab === "inventory" && (
                <InventorySection
                    rows={inventory}
                    productById={productById}
                    canManage={perm.manageInventory}
                    canDelete={perm.deleteInventory}
                    onCreate={openCreateInv}
                    onEdit={openEditInv}
                    onDelete={deleteInv}
                />
            )}

            {tab === "orders" && (
                <OrdersSection
                    orders={filteredOrders}
                    invBySku={invBySku}
                    productById={productById}
                    canManage={perm.manageOrders}
                    canTransition={perm.transitionOrder}
                    fulfillmentFilter={fulfillmentFilter}
                    onFulfillmentChange={setFulfillmentFilter}
                    onCreate={openCreateOrder}
                    onTransition={transitionOrder}
                />
            )}

            {/* ---------- Inventory Modal ---------- */}
            {editInv !== null && (
                <Modal title={editInv.id ? `Edit ${editInv.sku}` : "New Inventory Item"} onClose={closeInv}>
                    <form onSubmit={submitInv} className="space-y-4" data-testid="inventory-form">
                        <Field label="SKU">
                            <input
                                data-testid="inv-sku-input"
                                required
                                disabled={!!editInv.id}
                                value={invForm.sku}
                                onChange={(e) => setInvForm({ ...invForm, sku: e.target.value.toUpperCase() })}
                                className="cerb-input"
                                placeholder="ARG-TEL-01"
                            />
                            {editInv.id && (
                                <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider">
                                    SKU is the primary key and cannot be changed.
                                </div>
                            )}
                        </Field>
                        <Field label="Linked Product">
                            <select
                                data-testid="inv-product-select"
                                required
                                value={invForm.product_id}
                                onChange={(e) => setInvForm({ ...invForm, product_id: e.target.value })}
                                className="cerb-input"
                            >
                                <option value="" disabled>— Select a product —</option>
                                {products.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} — {p.lifecycle_status}{p.kill_switch_active ? " ⚠ KILL" : ""}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Quantity on Hand">
                                <input
                                    data-testid="inv-qty-input"
                                    type="number"
                                    min="0"
                                    required
                                    value={invForm.quantity_on_hand}
                                    onChange={(e) => setInvForm({ ...invForm, quantity_on_hand: e.target.value })}
                                    className="cerb-input"
                                />
                            </Field>
                            <Field label="Reserved">
                                <input
                                    data-testid="inv-reserved-input"
                                    type="number"
                                    min="0"
                                    value={invForm.reserved_quantity}
                                    onChange={(e) => setInvForm({ ...invForm, reserved_quantity: e.target.value })}
                                    className="cerb-input"
                                />
                            </Field>
                        </div>
                        <Field label="Location">
                            <input
                                data-testid="inv-location-input"
                                required
                                value={invForm.location}
                                onChange={(e) => setInvForm({ ...invForm, location: e.target.value })}
                                className="cerb-input"
                                placeholder="Warehouse A / Bin 12"
                            />
                        </Field>
                        {modalErr && <div className="text-red-400 text-xs font-mono">{modalErr}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={closeInv} className="cerb-btn-secondary" data-testid="inv-cancel-btn">Cancel</button>
                            <button type="submit" disabled={busy} className="cerb-btn-primary" data-testid="inv-save-btn">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* ---------- Order Create Modal ---------- */}
            {editOrder !== null && (
                <Modal title="New Order" onClose={closeOrder}>
                    <form onSubmit={submitOrder} className="space-y-4" data-testid="order-form">
                        <Field label="Customer Name">
                            <input
                                data-testid="order-customer-input"
                                required
                                value={orderForm.customer_name}
                                onChange={(e) => setOrderForm({ ...orderForm, customer_name: e.target.value })}
                                className="cerb-input"
                                placeholder="Helios Defense Co."
                            />
                        </Field>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2">
                                <Field label="SKU">
                                    <select
                                        data-testid="order-sku-select"
                                        required
                                        value={orderForm.sku}
                                        onChange={(e) => setOrderForm({ ...orderForm, sku: e.target.value })}
                                        className="cerb-input"
                                    >
                                        {inventory.map((i) => {
                                            const p = productById[i.product_id];
                                            const blocked = p && (p.kill_switch_active || p.lifecycle_status !== "Mission Ready");
                                            return (
                                                <option key={i.sku} value={i.sku}>
                                                    {i.sku} — {p?.name || "?"} {blocked ? "⚠ LOCKED" : ""}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </Field>
                            </div>
                            <Field label="Quantity">
                                <input
                                    data-testid="order-qty-input"
                                    type="number"
                                    min="1"
                                    required
                                    value={orderForm.quantity}
                                    onChange={(e) => setOrderForm({ ...orderForm, quantity: e.target.value })}
                                    className="cerb-input"
                                />
                            </Field>
                        </div>
                        <Field label="Fulfillment Type">
                            <div className="flex gap-2" data-testid="order-fulfillment-pills">
                                {FULFILLMENT_TYPES.map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        data-testid={`order-fulfillment-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                        onClick={() => setOrderForm({ ...orderForm, fulfillment_type: t })}
                                        className={`flex-1 border px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${
                                            orderForm.fulfillment_type === t
                                                ? "border-signal text-signal bg-signal/10"
                                                : "border-hairline text-snow-dim hover:border-snow"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </Field>
                        {/* Inline lock preview */}
                        {(() => {
                            const inv = invBySku[orderForm.sku];
                            const p = inv ? productById[inv.product_id] : null;
                            if (p && (p.kill_switch_active || p.lifecycle_status !== "Mission Ready")) {
                                return (
                                    <div className="border border-signal bg-signal/5 px-3 py-2 text-[11px] font-mono text-signal" data-testid="order-lock-warning">
                                        <span className="font-bold">MISSION READY LOCK:</span> {p.name} is {p.lifecycle_status}
                                        {p.kill_switch_active ? " with kill switch ACTIVE" : ""}. The order will be created in
                                        <span className="text-snow"> Override Pending </span>
                                        and routed to the executive queue.
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        {modalErr && <div className="text-red-400 text-xs font-mono">{modalErr}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={closeOrder} className="cerb-btn-secondary" data-testid="order-cancel-btn">Cancel</button>
                            <button type="submit" disabled={busy} className="cerb-btn-primary" data-testid="order-save-btn">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* ---------- Override Decision Modal ---------- */}
            {overrideTarget && (
                <Modal
                    title={overrideTarget.action === "approve" ? "Approve Override" : "Reject Override"}
                    onClose={() => { setOverrideTarget(null); setOverrideReason(""); }}
                    accent
                >
                    <div className="space-y-4" data-testid="override-modal">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-signal flex-shrink-0 mt-1" />
                            <div className="flex-1">
                                <div className="heading-display text-lg">
                                    {overrideTarget.action === "approve" ? "Authorize fulfillment exception?" : "Cancel this order?"}
                                </div>
                                <div className="text-sm text-snow-dim mt-2 font-mono leading-relaxed">
                                    Order: <span className="text-snow">{overrideTarget.order.order_id}</span> • {overrideTarget.order.customer_name}
                                </div>
                                <div className="text-xs text-snow-muted mt-1 font-mono">
                                    Block reason: <span className="text-signal">{overrideTarget.order.block_reason}</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="overline block mb-2">
                                Reason <span className="text-signal">*</span>
                            </label>
                            <textarea
                                data-testid="override-reason-input"
                                rows={3}
                                autoFocus
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                placeholder={overrideTarget.action === "approve"
                                    ? "e.g. Critical customer; risk accepted by exec board."
                                    : "e.g. Customer cancelled; will rebook when product is Mission Ready."}
                                className="cerb-input"
                                maxLength={500}
                            />
                            <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider flex justify-between">
                                <span>Required • will be written to audit_logs</span>
                                <span className="tabular-nums">{overrideReason.length}/500</span>
                            </div>
                        </div>
                        {modalErr && (
                            <div className="border border-red-500/40 bg-red-500/5 text-red-400 px-3 py-2 text-xs font-mono">{modalErr}</div>
                        )}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => { setOverrideTarget(null); setOverrideReason(""); }} className="cerb-btn-secondary" data-testid="override-cancel-btn">Cancel</button>
                            <button
                                onClick={submitOverride}
                                disabled={busy || overrideReason.trim().length < 3}
                                className={overrideTarget.action === "approve"
                                    ? "cerb-btn-primary"
                                    : "bg-red-500 text-snow hover:bg-red-600 px-6 py-2 font-bold uppercase tracking-[0.2em] text-xs"}
                                data-testid="override-confirm-btn"
                            >
                                {busy
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : overrideTarget.action === "approve" ? "Approve & Release" : "Reject & Cancel"}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// =================== Sub-components ===================

function KPI({ label, value, alert, accent, icon: Icon, testid }) {
    return (
        <div
            data-testid={testid}
            className={`border ${alert ? "border-signal bg-signal/5" : "border-[#2A2A2A] bg-jet-elev1"} p-5 flex items-center justify-between`}
        >
            <div>
                <div className="overline">{label}</div>
                <div className={`heading-display text-4xl mt-3 tabular-nums ${alert || accent ? "text-signal" : "text-snow"}`}>
                    {value}
                </div>
            </div>
            {Icon && <Icon className={`w-7 h-7 ${alert ? "text-signal animate-pulse-signal" : "text-snow-muted"}`} />}
        </div>
    );
}

function TabBtn({ active, onClick, testid, children }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 transition-colors ${
                active ? "border-signal text-signal" : "border-transparent text-snow-dim hover:text-snow"
            }`}
        >
            {children}
        </button>
    );
}

// ----- Inventory grid -----
function InventorySection({ rows, productById, canManage, canDelete, onCreate, onEdit, onDelete }) {
    return (
        <div className="space-y-4" data-testid="inventory-section">
            <div className="flex items-center justify-between">
                <div className="overline">Inventory Grid</div>
                <button
                    data-testid="create-inventory-btn"
                    onClick={onCreate}
                    disabled={!canManage}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-2 transition-colors ${
                        canManage
                            ? "bg-signal text-jet hover:bg-signal-hover"
                            : "bg-[#2A2A2A] text-snow-muted cursor-not-allowed"
                    }`}
                >
                    <Plus className="w-3 h-3" /> New SKU
                </button>
            </div>
            {/* Spec: Graphite (#2A2A2A) thin borders for the inventory grid. */}
            <div className="border border-[#2A2A2A] bg-jet-elev1">
                <table className="w-full" data-testid="inventory-table">
                    <thead>
                        <tr className="border-b border-[#2A2A2A]">
                            <Th>SKU</Th>
                            <Th>Linked Product</Th>
                            <Th>Status</Th>
                            <Th className="tabular-nums">On Hand</Th>
                            <Th className="tabular-nums">Reserved</Th>
                            <Th>Location</Th>
                            <Th>Kill Switch</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={8} className="px-5 py-12 text-center text-snow-muted text-xs font-mono">No inventory yet. Add your first SKU.</td></tr>
                        ) : rows.map((r) => {
                            const p = productById[r.product_id];
                            const grounded = p?.kill_switch_active;
                            return (
                                <tr key={r.id} data-testid={`inventory-row-${r.sku}`} className="border-b border-[#2A2A2A] hover:bg-white/5">
                                    <Td>
                                        <span className="font-mono text-sm text-signal tabular-nums">{r.sku}</span>
                                    </Td>
                                    <Td>
                                        <div className="font-mono text-sm">{p?.name || <span className="text-snow-muted">—</span>}</div>
                                        {p && <div className="text-[10px] text-snow-muted font-mono uppercase tracking-wider">{p.lifecycle_status}</div>}
                                    </Td>
                                    <Td><InventoryStatusBadge status={r.status} /></Td>
                                    <Td><span className="font-mono text-sm tabular-nums">{r.quantity_on_hand}</span></Td>
                                    <Td><span className="font-mono text-sm tabular-nums text-snow-dim">{r.reserved_quantity}</span></Td>
                                    <Td><span className="font-mono text-xs text-snow-dim">{r.location}</span></Td>
                                    <Td><KillSwitchPill active={!!grounded} /></Td>
                                    <Td>
                                        <div className="flex items-center gap-2">
                                            <button
                                                data-testid={`edit-inventory-${r.sku}`}
                                                onClick={() => canManage && onEdit(r)}
                                                disabled={!canManage}
                                                className={`p-1.5 border transition-colors ${
                                                    canManage
                                                        ? "border-[#2A2A2A] text-snow-dim hover:border-signal hover:text-signal"
                                                        : "border-[#2A2A2A] text-snow-muted cursor-not-allowed"
                                                }`}
                                            >
                                                <Edit2 className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                data-testid={`delete-inventory-${r.sku}`}
                                                onClick={() => canDelete && onDelete(r)}
                                                disabled={!canDelete}
                                                className={`p-1.5 border transition-colors ${
                                                    canDelete
                                                        ? "border-[#2A2A2A] text-snow-dim hover:border-red-500 hover:text-red-400"
                                                        : "border-[#2A2A2A] text-snow-muted cursor-not-allowed"
                                                }`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </Td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ----- Orders Section -----
function OrdersSection({ orders, invBySku, productById, canManage, canTransition, fulfillmentFilter, onFulfillmentChange, onCreate, onTransition }) {
    return (
        <div className="space-y-4" data-testid="orders-section">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="overline">Orders & Fulfillment</div>
                <div className="flex items-center gap-3">
                    {/* Drop-Ship toggle / segmented control */}
                    <div className="flex border border-hairline" data-testid="fulfillment-toggle">
                        {["All", "Internal", "Drop Ship"].map((opt) => (
                            <button
                                key={opt}
                                data-testid={`fulfillment-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => onFulfillmentChange(opt)}
                                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                                    fulfillmentFilter === opt
                                        ? "bg-signal text-jet"
                                        : "text-snow-dim hover:text-snow"
                                }`}
                            >
                                {opt}
                            </button>
                        ))}
                    </div>
                    <button
                        data-testid="create-order-btn"
                        onClick={onCreate}
                        disabled={!canManage}
                        className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-2 transition-colors ${
                            canManage
                                ? "bg-signal text-jet hover:bg-signal-hover"
                                : "bg-[#2A2A2A] text-snow-muted cursor-not-allowed"
                        }`}
                    >
                        <Plus className="w-3 h-3" /> New Order
                    </button>
                </div>
            </div>
            <div className="border border-[#2A2A2A] bg-jet-elev1">
                <table className="w-full" data-testid="orders-table">
                    <thead>
                        <tr className="border-b border-[#2A2A2A]">
                            <Th>Order</Th>
                            <Th>Customer</Th>
                            <Th>SKU / Product</Th>
                            <Th className="tabular-nums">Qty</Th>
                            <Th>Type</Th>
                            <Th>Status</Th>
                            <Th>Action</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.length === 0 ? (
                            <tr><td colSpan={7} className="px-5 py-12 text-center text-snow-muted text-xs font-mono">No orders match this filter.</td></tr>
                        ) : orders.map((o) => {
                            const inv = invBySku[o.sku];
                            const p = inv ? productById[inv.product_id] : null;
                            const blocked = p && (p.kill_switch_active || p.lifecycle_status !== "Mission Ready");
                            return (
                                <tr key={o.id} data-testid={`order-row-${o.order_id}`} className="border-b border-[#2A2A2A] hover:bg-white/5">
                                    <Td>
                                        <div className="font-mono text-sm text-signal tabular-nums">{o.order_id}</div>
                                        {o.order_status === "Override Pending" && (
                                            <div data-testid={`order-lock-ribbon-${o.order_id}`} className="mt-1 inline-flex items-center gap-1 border border-signal bg-signal/10 text-signal px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider">
                                                <ShieldOff className="w-3 h-3" /> Mission Ready Lock
                                            </div>
                                        )}
                                    </Td>
                                    <Td><span className="font-mono text-sm">{o.customer_name}</span></Td>
                                    <Td>
                                        <div className="font-mono text-xs text-snow-dim">{o.sku}</div>
                                        <div className="font-mono text-sm">{p?.name || "—"}</div>
                                    </Td>
                                    <Td><span className="font-mono tabular-nums">{o.quantity}</span></Td>
                                    <Td><FulfillmentBadge type={o.fulfillment_type} /></Td>
                                    <Td><OrderStatusBadge status={o.order_status} /></Td>
                                    <Td>
                                        <OrderActionMenu
                                            order={o}
                                            blocked={blocked}
                                            canTransition={canTransition}
                                            onTransition={onTransition}
                                        />
                                    </Td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function OrderActionMenu({ order, blocked, canTransition, onTransition }) {
    const status = order.order_status;
    if (!canTransition) {
        return <span className="text-[10px] text-snow-muted font-mono uppercase">Read-only</span>;
    }
    if (status === "Pending") {
        const allocateBlocked = blocked && !order.override_approved;
        return (
            <button
                data-testid={`allocate-btn-${order.order_id}`}
                disabled={allocateBlocked}
                onClick={() => !allocateBlocked && onTransition(order, "Allocated")}
                title={allocateBlocked ? `MISSION READY LOCK: ${order.block_reason || "product not Mission Ready"}` : "Reserve inventory"}
                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border flex items-center gap-1 transition-colors ${
                    allocateBlocked
                        ? "border-[#2A2A2A] text-snow-muted cursor-not-allowed"
                        : "border-signal text-signal hover:bg-signal hover:text-jet"
                }`}
            >
                Allocate <ArrowRight className="w-3 h-3" />
            </button>
        );
    }
    if (status === "Allocated") {
        const shipBlocked = blocked && !order.override_approved;
        return (
            <button
                data-testid={`ship-btn-${order.order_id}`}
                disabled={shipBlocked}
                onClick={() => !shipBlocked && onTransition(order, "Shipped")}
                title={shipBlocked ? `MISSION READY LOCK: ${order.block_reason || "product not Mission Ready"}` : "Decrement inventory and mark shipped"}
                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border flex items-center gap-1 transition-colors ${
                    shipBlocked
                        ? "border-[#2A2A2A] text-snow-muted cursor-not-allowed"
                        : "border-signal text-signal hover:bg-signal hover:text-jet"
                }`}
            >
                Ship <ArrowUpRight className="w-3 h-3" />
            </button>
        );
    }
    if (status === "Shipped") {
        return (
            <button
                data-testid={`deliver-btn-${order.order_id}`}
                onClick={() => onTransition(order, "Delivered")}
                className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-hairline text-snow-dim hover:border-signal hover:text-signal transition-colors flex items-center gap-1"
            >
                Mark Delivered <CheckCircle2 className="w-3 h-3" />
            </button>
        );
    }
    if (status === "Override Pending") {
        return <span className="text-[10px] text-signal font-mono uppercase tracking-wider">Awaiting Exec</span>;
    }
    return <span className="text-[10px] text-snow-muted font-mono uppercase tracking-wider">{status}</span>;
}

// ----- Override Queue Panel (executive surface) -----
function OverrideQueuePanel({ queue, onApprove, onReject }) {
    return (
        <div
            data-testid="order-override-queue-panel"
            className="border-2 border-signal bg-signal/5 animate-pulse-signal"
        >
            <div className="border-b border-signal/40 px-5 py-3 flex items-center justify-between bg-signal/10">
                <div className="flex items-center gap-3">
                    <ShieldOff className="w-4 h-4 text-signal" />
                    <span className="overline text-signal">Order Override Queue — Executive Action Required</span>
                </div>
                <span className="text-xs font-mono text-signal tabular-nums">{queue.length} blocked</span>
            </div>
            <ul className="divide-y divide-signal/20">
                {queue.map((q) => (
                    <li key={q.id} data-testid={`override-row-${q.order_id}`} className="px-5 py-4 flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="font-mono text-sm text-signal tabular-nums">{q.order_id}</span>
                                <FulfillmentBadge type={q.fulfillment_type} />
                                <span className="text-xs font-mono text-snow">{q.customer_name}</span>
                                <span className="text-[10px] font-mono text-snow-muted">→</span>
                                <span className="text-xs font-mono text-snow-dim">{q.product_name} ({q.sku})</span>
                                <span className="text-[10px] font-mono text-snow-muted tabular-nums">x{q.quantity}</span>
                            </div>
                            <div className="text-[11px] text-snow-dim mt-1 font-mono italic">"{q.block_reason}"</div>
                            <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider">
                                Waiting {q.hours_waiting}h • Risk: <span className="text-snow-dim">{q.risk_tier || "—"}</span>
                            </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                            <button
                                data-testid={`override-approve-${q.order_id}`}
                                onClick={() => onApprove(q)}
                                className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider bg-signal text-jet hover:bg-signal-hover flex items-center gap-1"
                            >
                                <CheckCircle2 className="w-3 h-3" /> Approve
                            </button>
                            <button
                                data-testid={`override-reject-${q.order_id}`}
                                onClick={() => onReject(q)}
                                className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-hairline text-snow-dim hover:border-red-500 hover:text-red-400 flex items-center gap-1"
                            >
                                <XCircle className="w-3 h-3" /> Reject
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ----- Badges -----
function InventoryStatusBadge({ status }) {
    const cls = status === "Available"
        ? "border-green-500/50 text-green-400 bg-green-500/5"
        : status === "Low Stock"
        ? "border-yellow-500/50 text-yellow-400 bg-yellow-500/5"
        : "border-red-500/50 text-red-400 bg-red-500/5";
    return (
        <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${cls}`}>
            {status}
        </span>
    );
}

function KillSwitchPill({ active }) {
    return active ? (
        <span data-testid="inv-kill-switch-active" className="inline-flex items-center gap-1 border border-signal text-signal bg-signal/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono animate-pulse-signal">
            <Power className="w-3 h-3" /> Grounded
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 border border-[#2A2A2A] text-snow-muted px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono">
            Clear
        </span>
    );
}

function FulfillmentBadge({ type }) {
    const cls = type === "Drop Ship"
        ? "border-purple-500/50 text-purple-300 bg-purple-500/5"
        : "border-blue-500/50 text-blue-300 bg-blue-500/5";
    return (
        <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${cls}`}>
            {type}
        </span>
    );
}

function OrderStatusBadge({ status }) {
    const map = {
        "Pending":          "border-yellow-500/50 text-yellow-400 bg-yellow-500/5",
        "Allocated":        "border-blue-500/50 text-blue-300 bg-blue-500/5",
        "Shipped":          "border-cyan-500/50 text-cyan-300 bg-cyan-500/5",
        "Delivered":        "border-green-500/50 text-green-400 bg-green-500/5",
        "Override Pending": "border-signal text-signal bg-signal/10",
        "Cancelled":        "border-[#2A2A2A] text-snow-muted bg-jet-elev1",
    };
    return (
        <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${map[status] || "border-[#2A2A2A] text-snow-dim"}`}>
            {status}
        </span>
    );
}

// ----- Common helpers -----
function Th({ children, className = "" }) {
    return <th className={`text-left px-5 py-3 overline font-normal ${className}`}>{children}</th>;
}
function Td({ children }) {
    return <td className="px-5 py-3 align-middle">{children}</td>;
}

function Modal({ title, children, onClose, accent }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-jet/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                className={`relative w-full max-w-xl bg-jet-elev1 border ${accent ? "border-signal" : "border-hairline"} max-h-[90vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={`flex items-center justify-between border-b ${accent ? "border-signal" : "border-hairline"} px-5 py-3`}>
                    <div>
                        <div className="overline">Action</div>
                        <div className="heading-display text-lg">{title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal" data-testid="modal-close-btn">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="overline block mb-2">{label}</label>
            {children}
        </div>
    );
}
